import React, { useState } from 'react';
import { AlignCenter, AlignLeft, AlignRight } from 'lucide-react';
import { useFormikContext } from 'formik';
import type { LinkInBio } from '@repo/lib';
import { useAuth } from '@dashboard/context/AuthContext/AuthContext';
import { useSellerThemes } from '@dashboard/hooks/queries/useThemeQueries';
import { ToggleGroup, ToggleGroupItem } from '@dashboard/components/ui/toggle-group';
import { AllThemesGallery } from '@dashboard/scenes/PageEdit/scenes/PageDesign/theming/AllThemesGallery';
import { ThemeCardGrid } from '@dashboard/scenes/PageEdit/scenes/PageDesign/theming/ThemeCardGrid';
import { ThemeCardGridSkeleton } from '@dashboard/scenes/PageEdit/scenes/PageDesign/theming/ThemeCardGridSkeleton';
import BrandingToggle from './BrandingToggle';
import SectionCard from './SectionCard';

const ALIGNS = [
  { value: 'left', label: 'Align left', Icon: AlignLeft },
  { value: 'center', label: 'Align center', Icon: AlignCenter },
  { value: 'right', label: 'Align right', Icon: AlignRight },
] as const;

const LOOKS = [
  { value: 'theme', label: 'Theme colors' },
  { value: 'brand', label: 'Primary color' },
] as const;

function OptionRow({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-2 py-3 sm:flex-row sm:items-center sm:justify-between sm:gap-4">
      <p className="text-sm font-medium">{label}</p>
      {children}
    </div>
  );
}

export default function ThemeSection() {
  const { seller } = useAuth();
  const { values, setFieldValue } = useFormikContext<LinkInBio>();
  const { data: themes, isLoading } = useSellerThemes();
  const [galleryOpen, setGalleryOpen] = useState(false);

  const defaultThemeId = seller?.defaultTheme ?? null;
  const chosenExists = themes?.some((theme: { _id: string }) => theme._id === values.theme);
  const currentThemeId = (chosenExists && values.theme) || defaultThemeId;
  // The store's default theme is saved as unset, so the page follows a later change of default.
  const selectTheme = (theme: { _id: string }) => {
    setFieldValue('theme', theme._id === defaultThemeId ? null : theme._id);
    setGalleryOpen(false);
  };

  if (galleryOpen) {
    return (
      <AllThemesGallery
        themes={themes ?? []}
        currentThemeId={currentThemeId}
        defaultThemeId={defaultThemeId}
        colorMode="light"
        onSelect={selectTheme}
        hideActions
        onClose={() => setGalleryOpen(false)}
      />
    );
  }

  return (
    <SectionCard title="Theme" description="Uses the fonts, colors and corners of a page theme.">
      {isLoading ? (
        <ThemeCardGridSkeleton />
      ) : (
        <ThemeCardGrid
          themes={themes ?? []}
          currentThemeId={currentThemeId}
          defaultThemeId={defaultThemeId}
          colorMode="light"
          onSelect={selectTheme}
          hideActions
          onViewAll={() => setGalleryOpen(true)}
        />
      )}

      <div className="divide-y rounded-lg border px-3">
        <OptionRow label="Background">
          <ToggleGroup
            type="single"
            variant="outline"
            value={values.look === 'brand' ? 'brand' : 'theme'}
            aria-label="Background"
            onValueChange={value => value && setFieldValue('look', value)}
          >
            {LOOKS.map(look => (
              <ToggleGroupItem key={look.value} value={look.value}>
                {look.label}
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </OptionRow>
        <OptionRow label="Text alignment">
          <ToggleGroup
            type="single"
            variant="outline"
            value={values.align ?? 'center'}
            aria-label="Text alignment"
            onValueChange={value => value && setFieldValue('align', value)}
          >
            {ALIGNS.map(({ value, label, Icon }) => (
              <ToggleGroupItem key={value} value={value} aria-label={label}>
                <Icon />
              </ToggleGroupItem>
            ))}
          </ToggleGroup>
        </OptionRow>
        <div className="py-3">
          <BrandingToggle />
        </div>
      </div>
    </SectionCard>
  );
}
